import { Property } from '@square/ignition';
import {
  ApiExplorerInput,
  ApiExplorerInputType,
} from '@squareup/dex-types-shared-api-explorer';
import {
  ApiExplorerField,
  ApiExplorerFieldContextProvider,
  ApiExplorerFieldLayout,
  ApiExplorerFileField,
  ApiExplorerObjectFieldProps,
} from '@squareup/dex-ui-shared-api-explorer-input-fields';
import {
  useApiExplorerEndpoint,
  useApiExplorerGraph,
  useApiExplorerInput,
} from '@squareup/dex-ui-shared-api-explorer-providers';
import { Box } from '@squareup/dex-ui-shared-base';
import { toIgnitionDataType } from '@squareup/dex-utils-oas';
import { filterAndSortApiExplorerProperties } from '@squareup/dex-utils-shared-api-explorer';
import { JSONPointer } from 'immutable-json-patch';
import React, { FC, useCallback, useMemo } from 'react';

interface BodyFieldProps {
  param: Property;
  inputState: ApiExplorerInput;
  onChangeState: ApiExplorerObjectFieldProps['onChange'];
}

const BodyField: FC<BodyFieldProps> = ({
  param,
  inputState,
  onChangeState,
}) => {
  const contextValue = useMemo(() => {
    return { isNullable: param.isSparseUpdatePayload };
  }, [param.isSparseUpdatePayload]);

  // Sparse updates only work on the body. If any property on the request
  // body is marked as an `x-sparse-update-payload, the entire field subtree is nullable
  return (
    <ApiExplorerFieldContextProvider value={contextValue}>
      <ApiExplorerField
        depth={0}
        property={param}
        currentValue={inputState.bodyParameters || {}}
        onChange={onChangeState}
        path="/bodyParameters"
      />
    </ApiExplorerFieldContextProvider>
  );
};

interface Props {}

const strings = {
  clientSecretDescription:
    'Secure identifier for your Square OAuth application that can be found on the OAuth page within your app settings in the Developer Dashboard.',
};

const ApiExplorerFieldContainer: FC<Props> = () => {
  const [inputState, dispatchInputState] = useApiExplorerInput();
  const graph = useApiExplorerGraph();

  const endpoint = useApiExplorerEndpoint();

  const bodyParameters = useMemo(() => {
    return filterAndSortApiExplorerProperties(
      endpoint?.requestBody?.properties ?? []
    );
  }, [endpoint?.requestBody?.properties]);

  const queryParameters = useMemo(() => {
    return filterAndSortApiExplorerProperties(endpoint?.queryParameters ?? []);
  }, [endpoint?.queryParameters]);

  const pathParameters = useMemo(() => {
    return filterAndSortApiExplorerProperties(endpoint?.pathParameters ?? []);
  }, [endpoint?.pathParameters]);

  const isAppSecretAuthType = useMemo(
    () => endpoint?.authType === 'applicationSecret',
    [endpoint?.authType]
  );

  const authParamProperty = useMemo(
    () =>
      new Property(
        {
          name: 'client_secret',
          type: toIgnitionDataType('string'),
          description: strings.clientSecretDescription,
          required: true,
        },
        graph
      ),
    [graph]
  );

  const onChangeState = useCallback(
    (
      value: ApiExplorerInputType,
      path: JSONPointer,
      op: 'add' | 'replace' | 'remove' = 'replace'
    ) => {
      dispatchInputState({
        type: 'patch',
        operation: {
          op,
          path,
          value,
        },
      });
    },
    [dispatchInputState]
  );

  // This should not be possible to reach
  if (!endpoint) {
    return null;
  }

  const pathParameterFields = pathParameters.map((param) => {
    return (
      <ApiExplorerField
        key={param.id}
        depth={0}
        property={param}
        currentValue={inputState.pathParameters || {}}
        onChange={onChangeState}
        path="/pathParameters"
      />
    );
  });

  const queryParameterFields = queryParameters.map((param) => {
    return (
      <ApiExplorerField
        key={param.id}
        depth={0}
        property={param}
        currentValue={inputState.queryParameters || {}}
        onChange={onChangeState}
        path="/queryParameters"
      />
    );
  });

  const hasParameters =
    pathParameterFields.length > 0 || queryParameterFields.length > 0;

  const authParamField = isAppSecretAuthType ? (
    <ApiExplorerField
      key={authParamProperty.id}
      depth={0}
      property={authParamProperty}
      currentValue={inputState.authParameters || {}}
      onChange={onChangeState}
      path="/authParameters"
    />
  ) : undefined;

  const bodyFields = bodyParameters.map((param) => {
    return (
      <BodyField
        key={param.id}
        param={param}
        inputState={inputState}
        onChangeState={onChangeState}
      />
    );
  });

  if (endpoint.supportsFileUpload) {
    bodyFields.unshift(
      <Box margin={{ vertical: '1x' }} key="file-upload">
        <ApiExplorerFileField
          currentValue={inputState.fileParameters || {}}
          onChange={onChangeState}
          path="/fileParameters"
        />
      </Box>
    );
  }

  const hasBody = endpoint.hasRequestBody && bodyFields.length > 0;

  return (
    <ApiExplorerFieldLayout
      queryPathParamFields={
        hasParameters ? (
          <>
            {pathParameterFields}
            {queryParameterFields}
          </>
        ) : undefined
      }
      authParamField={authParamField}
      bodyFields={hasBody ? bodyFields : undefined}
      onChange={onChangeState}
    />
  );
};

export { ApiExplorerFieldContainer };
